import React, { useState, useEffect } from 'react';
import { makeStyles, TableRow, Tooltip } from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import Tables from 'components/Table';
import { Button, Modal, Prompt, TooltipIcon } from 'components/Base';
import Router from 'components/Router/Router';
import store from 'store/store';
import Icons from 'components/Icons';
import api from 'api';
import { errHelper } from 'utils';
import { CustomerType } from 'store/modules/Customers/types';
import EditCasePage from 'pages/Case/EditCase';
import AssignClientModal from './Customers/AssignClientModal';

const useStyles = makeStyles({
  container: {
    marginTop: '1rem'
  },
  iconHolder: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '36px',
    height: '48px',
    marginRight: '64px'
  },
  cell: {
    width: '25rem'
  },
  iconCell: {
    marginRight: '64px'
  },
  iconDelete: {
    padding: '0rem 2rem 0rem 1rem'
  },
  warnIcon: {
    marginLeft: '0.5rem'
  }
});

const { connect, actions } = store;
const { useHistory } = Router;

const Case = ({
  id,
  name,
  clearCase,
  refreshCaseList,
  customers,
  hasInactiveFunds,
  advisers
}: {
  id: number;
  name: string;
  clearCase: () => void;
  refreshCaseList: any;
  customers: CustomerType[];
  hasInactiveFunds: boolean;
  advisers: any;
}) => {
  const [isDeletePromptOpen, setDeleteIsOpen] = useState(false);
  const [isClonePromptOpen, setCloneIsOpen] = useState(false);
  const [isFollowOnPromptOpen, setFollowOnIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [editCaseInfo, setEditCaseInfo] = useState(false);
  const [unassignedClientModalOpen, setUnassignedClientModalOpen] =
    useState(false);
  const [currentCustomers, setCurrentCustomers] = useState(customers);
  const [clientName, setClientName] = useState('');
  const classes = useStyles();
  const history = useHistory();

  useEffect(() => {
    setClientName(
      customers.length === 3
        ? customers[2].display_name
        : customers[0].display_name
    );
  }, [customers]);

  const editCase = (id: number) => {
    clearCase();
    history.push(`/${id}/risk-summary`);
  };

  const removeCase = () => {
    setIsProcessing(true);
    api
      .deleteCase({ portfolio: id })
      .then(() => {
        setIsProcessing(false);
        setDeleteIsOpen(false);
        refreshCaseList();
      })
      .catch((err: any) => {
        setIsProcessing(false);
        errHelper(err);
      });
  };

  const cloneCase = () => {
    setIsProcessing(true);
    api
      .cloneCase({ portfolio: id })
      .then(() => {
        setIsProcessing(false);
        setCloneIsOpen(false);
        refreshCaseList();
      })
      .catch((err: any) => {
        setIsProcessing(false);
        errHelper(err);
      });
  };

  const followOnCase = () => {
    setIsProcessing(true);
    api
      .createFollowOnCase({ portfolio: id })
      .then(() => {
        setIsProcessing(false);
        setFollowOnIsOpen(false);
        refreshCaseList();
      })
      .catch((err: any) => {
        setIsProcessing(false);
        errHelper(err);
      });
  };

  return (
    <>
      <Prompt
        isOpen={isDeletePromptOpen}
        title={`Are you sure you want to remove case: ${name}?`}
        confirmAction={removeCase}
        cancelAction={() => setDeleteIsOpen(false)}
        isProcessing={isProcessing}
      />
      <Prompt
        isOpen={isClonePromptOpen}
        title={`Are you sure you want to clone case: ${name}?`}
        confirmAction={cloneCase}
        cancelAction={() => setCloneIsOpen(false)}
        isProcessing={isProcessing}
      />
      <Prompt
        isOpen={isFollowOnPromptOpen}
        title={`Are you sure you want to create a follow on case based on case: ${name}?`}
        confirmAction={followOnCase}
        cancelAction={() => setFollowOnIsOpen(false)}
        isProcessing={isProcessing}
      />
      <TableRow
        key={id}
        onClick={() => editCase(id)}
        style={{ cursor: 'pointer' }}
      >
        <Tables.TableCell className={classes.cell}>
          <span id={`case-${id}`}>{name}</span>
          {hasInactiveFunds && (
            <TooltipIcon
              text="Funds are no longer supported. Action required"
              icon="warn"
              className={classes.warnIcon}
            />
          )}
        </Tables.TableCell>
        <Tables.TableCell className={classes.cell}>
          {clientName}
        </Tables.TableCell>
        <Tables.TableCell className={classes.cell}>
          {currentCustomers[0].adviser?.full_name || 'Unassigned'}
          {!currentCustomers[0].adviser && (
            <TooltipIcon
              text="Click to assign this client to an adviser."
              icon="warn"
              className={classes.warnIcon}
              onClick={e => {
                e.stopPropagation();
                setUnassignedClientModalOpen(true);
              }}
            />
          )}
        </Tables.TableCell>
        <Tables.TableCell>{id}</Tables.TableCell>

        <Tables.TableCell width="36px" className={classes.iconCell}>
          <div
            id={`edit-case-${id}`}
            className={classes.iconHolder}
            onClick={e => {
              e.stopPropagation();
              setEditCaseInfo(true);
            }}
          >
            <Tooltip title="Edit" arrow>
              <Icons.EditIcon width="2.2rem" style={{ cursor: 'pointer' }} />
            </Tooltip>
          </div>
        </Tables.TableCell>
        <Tables.TableCell width="36px" className={classes.iconCell}>
          <div
            className={classes.iconHolder}
            onClick={e => {
              e.stopPropagation();
              setCloneIsOpen(true);
            }}
          >
            <Tooltip title="Clone" arrow>
              <Icons.CopyIcon width="2.2rem" style={{ cursor: 'pointer' }} />
            </Tooltip>
          </div>
        </Tables.TableCell>
        <Tables.TableCell width="36px" className={classes.iconCell}>
          <div
            className={classes.iconHolder}
            onClick={e => {
              e.stopPropagation();
              setFollowOnIsOpen(true);
            }}
          >
            <Tooltip title="Follow-on" arrow>
              <Icons.FollowOnIcon
                width="2.2rem"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          </div>
        </Tables.TableCell>
        <Tables.TableCell width="36px" className={classes.iconDelete}>
          <div
            className={classes.iconHolder}
            onClick={e => {
              e.stopPropagation();
              setDeleteIsOpen(true);
            }}
          >
            <Tooltip title="Delete" arrow>
              <DeleteOutlineIcon
                width="2.2rem"
                data-id={`case-${id}`}
                id="remove-case"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          </div>
        </Tables.TableCell>
        <Tables.TableCell style={{ width: '105px' }}>
          <Button onClick={() => editCase(id)}>OPEN</Button>
        </Tables.TableCell>
      </TableRow>
      <Modal
        isOpen={editCaseInfo}
        closeModalFunc={() => setEditCaseInfo(false)}
      >
        <EditCasePage
          caseInfo={{ id, name, customers: currentCustomers }}
          setEditCaseInfo={setEditCaseInfo}
          editCaseInfo={editCaseInfo}
          refreshCaseList={refreshCaseList}
        />
      </Modal>
      <AssignClientModal
        isOpen={unassignedClientModalOpen}
        close={() => setUnassignedClientModalOpen(false)}
        clientName={clientName}
        advisers={advisers}
        clients={currentCustomers}
        updateClients={setCurrentCustomers}
      />
    </>
  );
};

export default connect(null, {
  clearCase: actions.caseActions.clearCase,
  setCase: actions.caseActions.setCaseInfo
})(Case);
